import React from 'react';
import type { ComponentType } from 'react';

// Layouts have heterogeneous props; the shell spreads pageProps into whichever layout is active.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LayoutComponent = ComponentType<any>;

export type PageWithLayout<P> = ComponentType<P> & { layout: LayoutComponent };

/**
 * Attach a layout to a page WITHOUT mutating the page component (ECOMM-8974).
 *
 * Pages used to declare layouts by assigning a static directly on their page component
 * (`SharedPage.layout = SomeLayout`). Several routes share one page component (e.g.
 * `src/dispensary/product` backs the /dispensary, /stores, /embedded-menu, and /kiosks PDPs), so
 * those page files were all mutating the SAME object. On the server, page modules evaluate
 * lazily in one shared module cache, which made the effective layout depend on which route was
 * hit first on a given pod — with SSR enabled this intermittently rendered the wrong chrome
 * (e.g. the dutchie marketplace global header on a white-label /stores PDP).
 *
 * This helper returns a NEW page component with `.layout` set on it, so each page file gets its
 * own object and routes can never clobber each other:
 *
 *   export default withLayout(DispensaryProductPage, CoreMenuLayout);
 *
 * Direct `.layout =` assignments in `pages/` are forbidden (enforced by with-layout.test.tsx).
 * Components may still self-assign `.layout` in their OWN module as a deterministic default for
 * routes that don't wrap.
 */
export function withLayout<P extends object>(
  // eslint-disable-next-line @typescript-eslint/naming-convention -- PascalCase because it's rendered as a JSX component
  Page: ComponentType<P>,
  layout: LayoutComponent
): PageWithLayout<P> {
  const WithLayout = (props: P): JSX.Element => <Page {...props} />;
  WithLayout.layout = layout;
  WithLayout.displayName = `WithLayout(${Page.displayName ?? Page.name ?? 'Page'})`;
  return WithLayout as PageWithLayout<P>;
}
